import Vue from "vue";
import Router from "vue-router";
import store from "./store";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: "/",
      component: () => import("./layouts/main/Main.vue"),
      meta: { isAuthed: true },

      children: [
        {
          path: "/",
          name: "home",
          component: () => import("./views/Home.vue"),
          meta: { roles: ["Admin"] },
        },
        {
          path: "clients",
          component: () => import("./layouts/category/Category.vue"),
          children: [
            {
              path: "/",
              name: "clients",
              component: () => import("./views/Clients/Clients.vue"),
              meta: {
                breadcrumb: [
                  { title: "Home" },
                  { title: "Clients", active: true },
                ],
                pageTitle: "Clients List",
                rule: "editor",
              },
            },
            {
              path: "add",
              name: "add-client",
              component: () => import("./views/Clients/AddClient.vue"),
              meta: {
                breadcrumb: [
                  { title: "Home" },
                  { title: "Clients", active: true },
                ],
                pageTitle: "Add Client",
                rule: "editor",
              },
            },
            {
              path: ":id",
              name: "client-details",
              component: () => import("./views/Clients/ClientDetails.vue"),
              meta: {
                breadcrumb: [
                  { title: "Home" },
                  { title: "Clients", active: true },
                ],
                pageTitle: "Client Details",
                rule: "editor",
              },
            },
          ],
        },
        {
          path: "products",
          component: () => import("./layouts/category/Category.vue"),
          children: [
            {
              path: "/",
              name: "products",
              component: () => import("./views/Products/Products.vue"),
              meta: {
                breadcrumb: [
                  { title: "Home" },
                  { title: "Products", active: true },
                ],
                pageTitle: "Products List",
                rule: "editor",
              },
            },
            {
              path: "add",
              name: "add-product",
              component: () => import("./views/Products/AddProduct.vue"),
              meta: {
                breadcrumb: [
                  { title: "Home" },
                  { title: "Products", active: true },
                ],
                pageTitle: "Add Product",
                rule: "editor",
              },
            },
          ],
        },
        {
          path: "users",
          component: () => import("./layouts/category/Category.vue"),
          meta: { roles: ["Admin"] },
          children: [
            {
              path: "/",
              name: "users",
              component: () => import("./views/Users/Users.vue"),
              meta: {
                breadcrumb: [
                  { title: "Home" },
                  { title: "Users", active: true },
                ],
                pageTitle: "Users List",
                rule: "editor",
              },
            },
            {
              path: "add",
              name: "add-user",
              component: () => import("./views/Users/AddUser.vue"),
              meta: {
                breadcrumb: [
                  { title: "Home" },
                  { title: "Users", active: true },
                ],
                pageTitle: "Add User",
                rule: "editor",
              },
            },
          ],
        },
        {
          path: "transactions",
          component: () => import("./layouts/category/Category.vue"),
          children: [
            {
              path: "/",
              name: "transactions",
              component: () => import("./views/Transactions/Transactions.vue"),
              meta: {
                breadcrumb: [
                  { title: "Home" },
                  { title: "Transactions", active: true },
                ],
                pageTitle: "Transactions List",
                rule: "editor",
              },
            },
          ],
        },
        {
          path: "/contract",
          name: "contract",
          component: () => import("@/views/pages/Contract.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Pages" },
              { title: "Contract", active: true },
            ],
            pageTitle: "Contract",
            rule: "editor",
          },
        },
      ],
    },
    {
      path: "",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        {
          path: "/login",
          name: "page-login",
          meta: { isNotAuthed: true },
          component: () => import("@/views/pages/Login.vue"),
        },
        {
          path: "/error-404",
          name: "page-error-404",
          component: () => import("@/views/pages/Error404.vue"),
        },
        {
          path: "/clientspace/:id",
          name: "client-details-public",
          component: () => import("./views/Clients/ClientDetailsPublic.vue"),
          meta: {
            breadcrumb: [{ title: "Home" }, { title: "Clients", active: true }],
            pageTitle: "Client Details",
            rule: "editor",
          },
        },
      ],
    },
    {
      path: "*",
      redirect: "/error-404",
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  // If route does not require authentication, handle it first
  if (to.matched.some((record) => record.meta.isNotAuthed)) {
    // If token exists, redirect to home, else proceed
    next(store.getters.getToken ? '/' : undefined);
    return;
  }

  // Check if the route requires authentication
  if (to.matched.some((record) => record.meta.isAuthed)) {
    // Check if the token exists in the store
    const token = store.getters.getToken;
    if (!token) {
      // If no token, redirect to login page
      next('/login');
      return;
    }

    // Check if the profile is already loaded
    if (!store.getters.isProfileLoaded) {
      try {
        // If not, dispatch USER_REQUEST to load user profile
        await store.dispatch("USER_REQUEST", token);
      } catch (error) {
        // If the profile cannot be loaded, redirect to login page
        next("/login");
        return;
      }
    }

    // Retrieve the user profile from the store (only for authenticated routes)
    const user = store.getters.getProfile;
    if (!user || !user._id) {
      next('/login');
      return;
    }

    // Check if the current route is restricted by role
    if (to.matched.some(record => record.meta.roles && !record.meta.roles.includes(user.role))) {
      // Redirect to '/products' if user does not have required role
      next('/products');
      return;
    }
  }

  // Check for any specific redirection rule in route meta
  if (to.matched.some((record) => record.meta.redirect)) {
    next(`/`);
    return;
  }

  // Proceed to the target route
  next();
});

router.afterEach(() => {
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

export default router;
